// SCORM 1.2 + 2004 LMS API surface that proxies cmi state to our backend.
//
// CRITICAL: SCORM content calls LMSInitialize() and then immediately makes
// SYNCHRONOUS LMSGetValue() calls for cmi.core.lesson_location / suspend_data /
// entry. If we pre-load state from the network INSIDE LMSInitialize via async
// fetch, the content reads empty strings and restarts from slide 1.
//
// → installScormApi(...) returns a Promise<uninstall>. The caller MUST await
//   that promise BEFORE mounting the course <iframe>. By the time the iframe
//   loads, window.API / window.API_1484_11 already have the hydrated cmi map,
//   so the very first synchronous LMSGetValue returns the resume position.

const BOOL_TRUE = "true";
const BOOL_FALSE = "false";

function deepClone(o) { return JSON.parse(JSON.stringify(o || {})); }

function buildScormApi({ apiBase, courseId, initialCmi, onStateChange }) {
  const cmi = { ...(initialCmi || {}) };
  let initialized = false;
  let terminated = false;
  let lastError = "0";
  let pendingPersist = false;
  let commitTimer = null;

  const setError = (code) => { lastError = String(code); };

  const persist = async (extra = {}) => {
    if (pendingPersist) return;
    pendingPersist = true;
    try {
      const lessonStatus =
        cmi["cmi.core.lesson_status"] ||
        cmi["cmi.completion_status"] ||
        cmi["cmi.success_status"] ||
        "";
      const progressMeasure = parseFloat(cmi["cmi.progress_measure"] || "");
      const scoreRaw = parseFloat(cmi["cmi.core.score.raw"] || cmi["cmi.score.raw"] || "");
      const scoreMax = parseFloat(cmi["cmi.core.score.max"] || cmi["cmi.score.max"] || "");
      const payload = {
        cmi: deepClone(cmi),
        lesson_location: cmi["cmi.core.lesson_location"] || cmi["cmi.location"] || "",
        suspend_data: cmi["cmi.suspend_data"] || "",
        lesson_status: lessonStatus,
        score_raw: Number.isFinite(scoreRaw) ? scoreRaw : null,
        score_max: Number.isFinite(scoreMax) ? scoreMax : null,
        score_min: parseFloat(cmi["cmi.core.score.min"] || cmi["cmi.score.min"] || "") || null,
        total_time: cmi["cmi.core.total_time"] || cmi["cmi.total_time"] || "",
        session_time: cmi["cmi.core.session_time"] || cmi["cmi.session_time"] || "",
        progress_measure: Number.isFinite(progressMeasure) ? progressMeasure : null,
        ...extra,
      };
      const r = await fetch(`${apiBase}/me/scorm/${courseId}/commit`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (r.ok && onStateChange) {
        const saved = await r.json().catch(() => payload);
        onStateChange(saved);
      }
    } catch (e) {
      // swallow; SCORM runtime must keep working even offline
    } finally {
      pendingPersist = false;
    }
  };

  const scheduleCommitLoop = () => {
    if (commitTimer) return;
    commitTimer = setInterval(() => {
      if (initialized && !terminated) persist();
    }, 15000);
  };
  const stopCommitLoop = () => { if (commitTimer) { clearInterval(commitTimer); commitTimer = null; } };

  // ---------------- SCORM 1.2 ----------------
  const log = (...a) => { try { if (typeof window !== "undefined") console.log("[SCORM]", ...a); } catch {} };
  const api12 = {
    LMSInitialize: function () {
      initialized = true;
      terminated = false;
      setError(0);
      scheduleCommitLoop();
      log("LMSInitialize() → true", { preloaded_keys: Object.keys(cmi) });
      return BOOL_TRUE;
    },
    LMSFinish: function () {
      if (!initialized) return BOOL_FALSE;
      terminated = true;
      stopCommitLoop();
      persist();
      setError(0);
      log("LMSFinish()");
      return BOOL_TRUE;
    },
    LMSGetValue: function (key) {
      setError(0);
      const v = cmi[String(key)];
      const out = v === undefined || v === null ? "" : String(v);
      log(`LMSGetValue("${key}") → "${out}"`);
      return out;
    },
    LMSSetValue: function (key, value) {
      cmi[String(key)] = String(value);
      setError(0);
      log(`LMSSetValue("${key}", "${String(value).slice(0, 60)}${String(value).length > 60 ? "…" : ""}")`);
      return BOOL_TRUE;
    },
    LMSCommit: function () { persist(); setError(0); log("LMSCommit()"); return BOOL_TRUE; },
    LMSGetLastError: function () { return lastError; },
    LMSGetErrorString: function () { return ""; },
    LMSGetDiagnostic: function () { return ""; },
  };

  // ---------------- SCORM 2004 ----------------
  const api2004 = {
    Initialize: function () { return api12.LMSInitialize(); },
    Terminate: function () { return api12.LMSFinish(); },
    GetValue: function (key) { return api12.LMSGetValue(key); },
    SetValue: function (key, value) { return api12.LMSSetValue(key, value); },
    Commit: function () { return api12.LMSCommit(); },
    GetLastError: function () { return lastError; },
    GetErrorString: function () { return ""; },
    GetDiagnostic: function () { return ""; },
  };

  return { api12, api2004, persist, getCmi: () => deepClone(cmi) };
}

// Fetch the persisted SCORM state from backend so we can hand the content a
// pre-populated cmi map on the first synchronous GetValue.
async function fetchInitialCmi({ apiBase, courseId }) {
  try {
    const r = await fetch(`${apiBase}/me/scorm/${courseId}`, { credentials: "include" });
    if (!r.ok) return {};
    const data = await r.json();
    const cmi = (data && data.cmi && typeof data.cmi === "object") ? { ...data.cmi } : {};
    // Mirror primary keys if cmi map is empty (first session after legacy commit).
    if (!cmi["cmi.core.lesson_location"] && data?.lesson_location) {
      cmi["cmi.core.lesson_location"] = data.lesson_location;
      cmi["cmi.location"] = data.lesson_location;
    }
    if (!cmi["cmi.suspend_data"] && data?.suspend_data) cmi["cmi.suspend_data"] = data.suspend_data;
    if (!cmi["cmi.core.lesson_status"] && data?.lesson_status) cmi["cmi.core.lesson_status"] = data.lesson_status;
    // Set entry flag synchronously so content knows to resume.
    const hasResumeState = !!(data?.lesson_location || data?.suspend_data);
    cmi["cmi.core.entry"] = hasResumeState ? "resume" : "ab-initio";
    cmi["cmi.entry"] = hasResumeState ? "resume" : "";
    cmi["cmi.core.credit"] = cmi["cmi.core.credit"] || "credit";
    cmi["cmi.core.lesson_mode"] = cmi["cmi.core.lesson_mode"] || "normal";
    return cmi;
  } catch {
    return {};
  }
}

// Returns a Promise<uninstall>. Caller MUST await before mounting the iframe.
export async function installScormApi({ apiBase, courseId, onStateChange }) {
  if (typeof window === "undefined") return () => {};
  const initialCmi = await fetchInitialCmi({ apiBase, courseId });
  const { api12, api2004 } = buildScormApi({ apiBase, courseId, initialCmi, onStateChange });

  // Save originals so cleanup is safe if multiple players mount.
  const prev12 = window.API;
  const prev2004 = window.API_1484_11;
  window.API = api12;
  window.API_1484_11 = api2004;

  // Notify state listeners immediately so the player UI can show resume status.
  if (onStateChange) {
    onStateChange({
      lesson_status: initialCmi["cmi.core.lesson_status"] || "",
      lesson_location: initialCmi["cmi.core.lesson_location"] || "",
      resumed: initialCmi["cmi.core.entry"] === "resume",
    });
  }

  const onUnload = () => { try { api12.LMSCommit(); api12.LMSFinish(); } catch (e) { /* noop */ } };
  window.addEventListener("beforeunload", onUnload);

  return () => {
    window.removeEventListener("beforeunload", onUnload);
    try { api12.LMSCommit(); api12.LMSFinish(); } catch (e) { /* noop */ }
    window.API = prev12;
    window.API_1484_11 = prev2004;
  };
}
